import { Metadata } from './types';

export type WithId<T> = T & { id: string };

/**
 * Wrapper for RTK Query responses that use frontend entity types (which already include `id`).
 * Use this instead of `ApiResponseWithId` when the entity type is from a service's `types.ts`.
 */
export type EntityResponse<T> = {
  data: T;
  metadata?: Metadata | undefined;
};

type ApiResponse<T> = {
  data: T;
  metadata?: Record<string, unknown> | undefined;
};

export type ApiResponseWithId<T> = {
  data: T extends (infer U)[] ? WithId<U>[] : WithId<T>;
  metadata?: Metadata | undefined;
};

export const attachId = <T>(
  response: ApiResponse<T>,
  fn: (entry: T) => string
): ApiResponseWithId<T> =>
  ({
    data: {
      ...response.data,
      id: fn(response.data),
    },
    metadata: {
      ...response.metadata,
      id: {
        hide: true,
        label: 'ID',
        editable: false,
        type: 'text',
      },
    },
  }) as ApiResponseWithId<T>;

export const attachIds = <T>(
  response: ApiResponse<T[]>,
  fn: (entry: T) => string
): ApiResponseWithId<T[]> =>
  ({
    data: response.data.map(entry => ({
      ...entry,
      id: fn(entry),
    })),
    metadata: {
      ...response.metadata,
      id: {
        hide: true,
        label: 'ID',
        editable: false,
        type: 'text',
      },
    },
  }) as ApiResponseWithId<T[]>;
